import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

// The admin session is the raw Google ID token (a JWT). We persist it in
// localStorage so the session survives page navigations, reloads, and new tabs
// — until the user signs out or the token's own `exp` claim passes.
const TOKEN_KEY = 'admin_token';

// Mirrors the backend's `admin_email` (apps/spacenative-api app/core/config.py).
// Only this identity is treated as an admin and unlocks the admin app.
const ADMIN_EMAIL = (process.env.REACT_APP_ADMIN_EMAIL || 'parthmerchant8@gmail.com').toLowerCase();

const AuthContext = createContext(null);

function decodeJwt(token) {
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    return JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/')));
  } catch {
    return null;
  }
}

// Read the token from storage and return a session object only if it is present
// and not expired. Expired/invalid tokens are purged. Falls back to migrating a
// legacy sessionStorage token (previous versions stored it there).
function readValidSession() {
  let token = null;
  try {
    token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      const legacy = sessionStorage.getItem(TOKEN_KEY);
      if (legacy) { localStorage.setItem(TOKEN_KEY, legacy); sessionStorage.removeItem(TOKEN_KEY); token = legacy; }
    }
  } catch { token = null; }

  if (!token) return null;
  const claims = decodeJwt(token);
  if (!claims || !claims.exp) return null;
  if (Date.now() >= claims.exp * 1000) {
    try { localStorage.removeItem(TOKEN_KEY); } catch {}
    return null;
  }
  return { token, email: claims.email || '', name: claims.name || '', picture: claims.picture || '', exp: claims.exp, iat: claims.iat || null };
}

export function AuthProvider({ children }) {
  const [session, setSession] = useState(() => readValidSession());
  const timerRef = useRef(null);

  // Auto sign-out exactly when the token expires.
  useEffect(() => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    if (!session?.exp) return undefined;
    const ms = session.exp * 1000 - Date.now();
    if (ms <= 0) { setSession(null); return undefined; }
    // setTimeout caps near ~24.8 days; Google tokens are ~1h so this is safe.
    timerRef.current = setTimeout(() => {
      try { localStorage.removeItem(TOKEN_KEY); } catch {}
      setSession(null);
    }, ms);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [session]);

  // Keep auth state in sync across tabs/windows.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === TOKEN_KEY || e.key === null) setSession(readValidSession());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const signIn = useCallback((token) => {
    try { localStorage.setItem(TOKEN_KEY, token); } catch {}
    setSession(readValidSession());
  }, []);

  const signOut = useCallback(() => {
    try { localStorage.removeItem(TOKEN_KEY); } catch {}
    try { sessionStorage.removeItem(TOKEN_KEY); } catch {}
    setSession(null);
  }, []);

  const value = {
    isAuthed: !!session,
    isAdmin: !!session && (session.email || '').toLowerCase() === ADMIN_EMAIL,
    email: session?.email || '',
    name: session?.name || '',
    picture: session?.picture || '',
    token: session?.token || null,
    iat: session?.iat || null,
    signIn,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
